import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export interface BreadcrumbItem {
  label: string;
  path: string;
  isActive: boolean;
}

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const { t } = useTranslation();

  return useMemo(() => {
    // Don't show breadcrumbs on login page
    if (location.pathname === "/login") {
      return [];
    }

    const pathname = location.pathname;
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: t("groupList"),
        path: "/",
        isActive: pathname === "/",
      },
    ];

    // Don't add more breadcrumbs if we're just on home
    if (pathname === "/") {
      return breadcrumbs;
    }

    // Check if this is a machine route with group (group/machine pages and their tabs)
    const machineInGroupMatch = pathname.match(
      /^\/groups\/([^/]+)\/machines\/([^/]+)(\/.*)?$/,
    );
    if (machineInGroupMatch) {
      const groupId = machineInGroupMatch[1];
      const machineId = machineInGroupMatch[2];

      // Add Group breadcrumb
      breadcrumbs.push({
        label: "Group",
        path: `/groups/${groupId}`,
        isActive: false,
      });

      // Add Machine breadcrumb - always active for this route pattern
      breadcrumbs.push({
        label: "Machine",
        path: `/groups/${groupId}/machines/${machineId}`,
        isActive: true,
      });

      return breadcrumbs;
    }

    // Check if this is a group route (group details and tabs)
    const groupMatch = pathname.match(/^\/groups\/([^/]+)(\/.*)?$/);
    if (groupMatch) {
      const groupId = groupMatch[1];

      // Add Group breadcrumb - always active for this route pattern
      breadcrumbs.push({
        label: "Group",
        path: `/groups/${groupId}`,
        isActive: true,
      });

      return breadcrumbs;
    }

    // Check if this is a machine route without group
    const machineMatch = pathname.match(/^\/machines\/([^/]+)(\/.*)?$/);
    if (machineMatch) {
      const machineId = machineMatch[1];

      if (machineId === "search") {
        breadcrumbs.push({
          label: t("acrossGroupMachines.pageTitle"),
          path: "/machines/search",
          isActive: true,
        });
      } else {
        breadcrumbs.push({
          label: t("groupDetailTab.machineTab"),
          path: `/machines/${machineId}`,
          isActive: true,
        });
      }

      return breadcrumbs;
    }

    // Search user pages
    if (pathname === "/search-user") {
      const isManage = location.search.includes("mode=manage");
      breadcrumbs.push({
        label: isManage
          ? t("permission.USER_MANAGE")
          : t("permission.USER_SEARCH"),
        path: pathname + location.search,
        isActive: true,
      });
      return breadcrumbs;
    }

    // Manage TOU
    if (pathname === "/manage-tou") {
      breadcrumbs.push({
        label: t("permission.MANAGE_TOU"),
        path: pathname,
        isActive: true,
      });
      return breadcrumbs;
    }

    // Create announcement
    if (pathname === "/create-announcement") {
      breadcrumbs.push({
        label: t("permission.ANNOUNCEMENTS"),
        path: pathname,
        isActive: true,
      });
      return breadcrumbs;
    }

    // Trademark
    if (pathname === "/trademark") {
      breadcrumbs.push({
        label: t("tradeMark.title"),
        path: pathname,
        isActive: true,
      });
      return breadcrumbs;
    }

    //License
    if (pathname === "/license") {
      breadcrumbs.push({
        label: t("license.title"),
        path: pathname,
        isActive: true,
      });
      return breadcrumbs;
    }

    //Translations
    if (pathname === "/manage-translations") {
      breadcrumbs.push({
        label: t("translations"),
        path: pathname,
        isActive: true,
      });
      return breadcrumbs;
    }

    //Periodic Maintenance
    if (pathname === "/manage-periodic-maintenance") {
      breadcrumbs.push({
        label: t("managePeriodicMaintenance"),
        path: pathname,
        isActive: true,
      });
      return breadcrumbs;
    }

    return [];
  }, [location.pathname, location.search, t]);
}
